import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tooltip,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  ReceiptLongRounded as ReceiptLongIcon,
  AssignmentRounded as RegisterIcon,
  PictureAsPdfRounded as PdfIcon,
  Inventory2Rounded as StockIcon,
  TuneRounded as TuneIcon,
  LayersRounded as FloorIcon,
} from "@mui/icons-material";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { usePawnMode } from "../../Context/PawnModeContext";
import {
  reportPageSx,
  reportFixedSx,
  reportResultsCardSx,
  reportWideTableSx,
} from "./reportLayout";
import ReportDateWindow from "./reportDateWindow";
import ReportSortControl, { DEFAULT_SORT, sortRows } from "./reportSort";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue/White Design Tokens (shared with PaymentScreen/PaymentReport) ──────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE = "#FFFFFF";
const SUCCESS = "#059669";

// Every pledge is redeemable over a fixed 12-month term (business rule).
const REDEMPTION_PERIOD = "12 months";

const TODAY = () => dayjs().format("YYYY-MM-DD");
// Reports open on the running month, not on today alone: the first of the
// current month through today. A single-day-today default made every register
// look empty first thing in the morning.
const MONTH_START = () => dayjs().startOf("month").format("YYYY-MM-DD");

// ── Formatting helpers ──────────────────────────────────────────────────────
const fmtINR = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};
// Weights always carry three decimals in this report.
const fmtWeight = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
};
// Metal rate per gram — usually a whole number, so trim trailing decimals.
const fmtRate = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Government-approved official monthly interest cap (%). Loans ARE written
// above this rate, but an OFFICIAL report must never print the true rate — it
// prints the store's master rate instead (itself clamped to the cap in Pawn
// Configuration), and the cap itself if settings could not be read. Unofficial
// reports always print the actual rate.
const OFFICIAL_RATE_CAP = 1.16;

// ─── Sorting ─────────────────────────────────────────────────────────────────
// The register's rows are LOANS, so "date" is the loan's own date column and
// "amount" its principal — the same four ideas the other reports sort on.
const SORT_OPTIONS = [
  { key: "date", label: "Date of Loan" },
  { key: "amount", label: "Amount" },
  { key: "customer", label: "Customer Name" },
  { key: "voucher", label: "Pledge No" },
];
const ROW_SORT = {
  date: (r) => r.loanDate,
  amount: (r) => r.principal,
  customer: (r) => r.customerName,
  voucher: (r) => r.pledgeNo,
};

// Run an async mapper over items with a bounded concurrency so we don't fire
// hundreds of detail/receipt requests at once when a wide date range is picked.
async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = new Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (cursor < items.length) {
        const idx = cursor++;
        results[idx] = await worker(items[idx], idx);
      }
    });
  await Promise.all(runners);
  return results;
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
const ReceiptReport = ({
  embedded = false,
  stockFilter: stockFilterProp = null,
}) => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  // Pawn mode: 1 = official, 2 = unofficial. Official mode sees status 1 + 3
  // and never unofficial (status 2) records; unofficial mode sees status 2 + 3
  // (status 3 is official-clean and shared by both books). The filtering
  // happens server-side via the `mode` query param.
  const { mode: pawnMode } = usePawnMode() || {};

  // ── Stock Report mode ───────────────────────────────────────────────────────
  // Stock Report mode is driven either by a `stockFilter` prop (when this report
  // is embedded inside the Stock Check screen) or by the ?stock= query param
  // (when opened as a standalone route). Either way the register lists every
  // loan in that stock-check state — ignoring the date window — instead of the
  // receipts in a date range, so THIS report doubles as the "Stock Report".
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStock = searchParams.get("stock");
  const urlStock =
    rawStock === "checked" || rawStock === "unchecked" ? rawStock : null;
  const stockFilter = stockFilterProp || urlStock;
  const clearStock = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("stock");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Filter state ──────────────────────────────────────────────────────────
  // The date window is applied to the RECEIPT date (when the customer paid) —
  // so the register lists the receipts made in the window, grouped under their
  // loan, no matter when that loan was pledged. It opens on the running month
  // (1st → today) rather than on today alone.
  //
  // A caller (e.g. the dashboard's "Recent Receipts" → View report link) may
  // seed the window through navigation state, so the report can open already
  // scoped to some other range instead of the default month-to-date view. Same
  // contract as the Payment Report: { dateMode, singleDate, fromDate, toDate }.
  const { state: navState } = useLocation();
  const [dateMode, setDateMode] = useState(navState?.dateMode || "range");
  const [singleDate, setSingleDate] = useState(navState?.singleDate || TODAY());
  const [fromDate, setFromDate] = useState(navState?.fromDate || MONTH_START());
  const [toDate, setToDate] = useState(navState?.toDate || TODAY());
  const [customerSearch, setCustomerSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");

  // ── Report options (checkbox toolbar, ported from the legacy VB pledge book) ─
  // These fall in two groups.
  //
  // (A) FULLY WIRED — each changes what the register actually shows. Most are
  // pure client-side view/filter toggles (no refetch); `showAll` widens the
  // date window so it participates in the fetch.
  const [showAll, setShowAll] = useState(false); // ignore the date window
  const [hideTotal, setHideTotal] = useState(false); // drop the totals footer
  const [showMobile, setShowMobile] = useState(false); // mobile under customer
  const [showRate, setShowRate] = useState(false); // metal rate ₹/g column
  const [showPurity, setShowPurity] = useState(false); // add a Purity column
  const [summaryView, setSummaryView] = useState(false); // collapse receipt lines
  const [showTnC, setShowTnC] = useState(false); // print terms & conditions
  const [crossedLoanPeriod, setCrossedLoanPeriod] = useState(false); // overdue only
  const [floorWiseSummary, setFloorWiseSummary] = useState(false); // floor subtotals
  //
  // "Show all" is a one-click "everything on": it widens the date window to the
  // whole history AND switches the three optional columns (Mobile / Rate ₹/g /
  // Purity) on together, the way the Payment Report's "Show All" does. Unticking
  // it puts all four back off.
  const toggleShowAll = (next) => {
    setShowAll(next);
    setShowMobile(next);
    setShowRate(next);
    setShowPurity(next);
  };
  //
  // (B) RENDERED + STATE ONLY — legacy toggles whose exact VB semantics aren't
  // settled yet. They hold state and round-trip through Reset, but deliberately
  // do NOT alter the report output until their behaviour is specced. Marked with
  // a "not yet wired" tooltip in the UI so it's honest about what they do.
  const [onlySettleInterest, setOnlySettleInterest] = useState(false);
  const [asPerDate, setAsPerDate] = useState(false);
  const [newFormat, setNewFormat] = useState(false);
  const [settleDetailsAsOnDate, setSettleDetailsAsOnDate] = useState(false);
  const [bankPledge, setBankPledge] = useState(false);
  const [maximumLoan, setMaximumLoan] = useState(false);
  // Top-left dropdown + "Mon 15 →" field — rendered to match the old layout but
  // inert for now (behaviour to be defined later).
  const [reportVariant, setReportVariant] = useState("");
  const [redemptionMonths, setRedemptionMonths] = useState(15);

  // Row order chosen from the sort button beside View — client-side only.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // ── Data state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Caches keyed by store so re-applying filters doesn't re-hit the network.
  const detailCacheRef = useRef(new Map()); // voucher_no -> { gross, valuation, pcs, description, customer }
  const customerMapRef = useRef(new Map()); // cust_id -> customer record

  const showSnackbar = useCallback(
    (message, severity = "info") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  // Fetch (once per store) the customer directory so we can resolve names and
  // addresses by cust_id without a detail call per row, and power the search.
  const loadCustomerMap = useCallback(async () => {
    if (storeId == null) return;
    if (customerMapRef.current.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
        { params: { limit: 5000, sort: "ASC" } },
      );
      const list = res.data?.data || [];
      const map = new Map();
      list.forEach((c) => map.set(c.id, c));
      customerMapRef.current = map;
    } catch {
      // Non-fatal: detail calls still backfill customer info.
    }
  }, [storeId]);

  // Fetch (once per store) the master interest % from pawn settings. In
  // official mode any actual rate above the 1.16% ceiling is displayed as this
  // master rate instead; unofficial mode always shows the actual rate.
  const masterIntRef = useRef(null);
  const loadMasterInt = useCallback(async () => {
    if (storeId == null || masterIntRef.current != null) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      const v = parseFloat(res.data?.data?.master_int);
      if (!isNaN(v)) masterIntRef.current = v;
    } catch {
      // Non-fatal: rates fall back to the actual stored value.
    }
  }, [storeId]);

  // Fetch (once per store) the floor master so FloorWise Summary can label its
  // subtotal rows with the floor NAME instead of a bare floor_id.
  const floorMapRef = useRef(new Map()); // floor_id -> floor name
  const loadFloors = useCallback(async () => {
    if (storeId == null || floorMapRef.current.size > 0) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/jewel-master/floors/store/${storeId}`,
      );
      const list = res.data?.data || [];
      const map = new Map();
      list.forEach((f) => map.set(f.id, f.name));
      floorMapRef.current = map;
    } catch {
      // Non-fatal: floors fall back to "Floor <id>".
    }
  }, [storeId]);

  // Pull the per-ticket ornament + customer info. Gross weight, valuation,
  // pieces and the article description live only on the loan detail. Cached by
  // voucher number + series — the official and unofficial books number
  // independently, so the same voucher_no exists in both.
  const loadTicketDetail = useCallback(
    async (voucherNo, rowStatus) => {
      const cacheKey = `${voucherNo}|${rowStatus ?? ""}`;
      if (detailCacheRef.current.has(cacheKey)) {
        return detailCacheRef.current.get(cacheKey);
      }
      const empty = {
        gross: null,
        valuation: null,
        pcs: null,
        description: "—",
        purity: null,
        rate: null,
        tare: null,
        floorId: null,
        customer: null,
      };
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/payments/loan/${encodeURIComponent(
            voucherNo,
          )}`,
          // The row's OWN series pins the lookup to the right loan.
          { params: { store_id: storeId, status: rowStatus ?? undefined } },
        );
        if (!(res.data?.success && res.data?.statusCode === 200)) {
          detailCacheRef.current.set(cacheKey, empty);
          return empty;
        }
        const ornaments = res.data.data?.ornaments_info || [];
        const gross = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_gross) || 0),
          0,
        );
        const valuation = ornaments.reduce(
          (s, o) => s + (parseFloat(o.item_value) || 0),
          0,
        );
        const pcs = ornaments.reduce(
          (s, o) => s + (parseInt(o.item_pcs, 10) || 0),
          0,
        );
        // "RING + CHAIN + NECKLACE" — item names joined with a plus.
        const description =
          ornaments
            .map((o) => (o.item_name || "").trim())
            .filter(Boolean)
            .join(" + ") || "—";
        // Distinct purities across the loan's ornaments, e.g. "91.6 / 75.0".
        const purity =
          [
            ...new Set(
              ornaments
                .map((o) =>
                  o.purity_percentage != null
                    ? parseFloat(o.purity_percentage)
                    : null,
                )
                .filter((v) => v != null && !isNaN(v)),
            ),
          ]
            .sort((a, b) => b - a)
            .join(" / ") || null;
        // Distinct METAL rates per gram across the loan's ornaments, e.g.
        // "6,300 / 5,800" — this is what the "Show Rate" column prints (same as
        // the Payment Report). NOT the interest rate, which is its own column.
        // First-seen order is preserved so it reads in ornament order.
        const rateSet = [];
        ornaments.forEach((o) => {
          const r = fmtRate(o.metal_type_rate);
          if (r != null && !rateSet.includes(r)) rateSet.push(r);
        });
        const rate = rateSet.length ? rateSet.join(" / ") : null;
        // Total tare weight across the loan's ornaments.
        const tareVals = ornaments
          .map((o) => (o.tare_wt != null ? parseFloat(o.tare_wt) : null))
          .filter((v) => v != null && !isNaN(v));
        const tare = tareVals.length
          ? tareVals.reduce((s, v) => s + v, 0)
          : null;
        // Primary floor = the first ornament that carries a floor_id. FloorWise
        // Summary groups each loan under this floor.
        const floorId =
          ornaments.find((o) => o.floor_id != null)?.floor_id ?? null;
        const detail = {
          gross,
          valuation,
          pcs,
          description,
          purity,
          rate,
          tare,
          floorId,
          customer: res.data.data?.customer_info || null,
        };
        detailCacheRef.current.set(cacheKey, detail);
        return detail;
      } catch {
        detailCacheRef.current.set(cacheKey, empty);
        return empty;
      }
    },
    [storeId],
  );

  // ── Apply filters & build the report ──────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null) {
      showSnackbar("No store selected.", "warning");
      return;
    }

    if (dateMode === "range" && fromDate && toDate && fromDate > toDate) {
      showSnackbar("'From' date cannot be after 'To' date.", "warning");
      return;
    }

    setLoading(true);
    try {
      await Promise.all([loadCustomerMap(), loadMasterInt(), loadFloors()]);

      // "Show all" ignores the picked date window and pulls the entire history;
      // otherwise the window is the single day / From–To range.
      const startDate = showAll
        ? "1900-01-01"
        : dateMode === "single"
        ? singleDate
        : fromDate;
      const endDate = showAll
        ? "2999-12-31"
        : dateMode === "single"
        ? singleDate
        : toDate;
      const wantUnofficial = Number(pawnMode) === 2;

      // One register row per loan, keyed by voucher number.
      const byLoan = new Map();
      if (stockFilter) {
        // Stock Report: every loan whose stock is (un)checked, date window
        // ignored. The backend returns the same grouped { loan, receipts } shape
        // this report renders, so no client-side grouping is needed. Loans with
        // no receipts still appear as a register row (receipts: []).
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/stock-check/register/${storeId}`,
          {
            params: {
              status: stockFilter,
              // Mode-scoped server-side, same as every other report source:
              // official mode must never list unofficial pouches.
              mode: wantUnofficial ? 2 : 1,
            },
          },
        );
        const grouped = res.data?.data || [];
        for (const g of grouped) {
          if (!g?.loan?.voucher_no) continue;
          byLoan.set(g.loan.voucher_no, {
            loan: g.loan,
            receipts: g.receipts || [],
          });
        }
      } else {
        // Every receipt whose RECEIPT DATE falls in the window (mode-scoped
        // server-side). A receipt appears here on the day the customer paid,
        // regardless of when its loan was pledged — that is the point of this
        // report. Repayment receipts AND the "H" settlement receipt are included.
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/receipts/store/${storeId}`,
          {
            params: {
              mode: wantUnofficial ? 2 : 1,
              startDate,
              endDate,
            },
          },
        );
        const allReceipts = res.data?.data || [];
        // Group the window's receipts by their parent loan — one register row
        // per loan, with only that loan's in-window receipts nested under it.
        for (const rc of allReceipts) {
          const key = rc.voucher_no;
          if (!key) continue;
          if (!byLoan.has(key)) byLoan.set(key, { loan: rc, receipts: [] });
          byLoan.get(key).receipts.push(rc);
        }
      }

      const custQuery = customerSearch.trim().toLowerCase();
      const ticketQuery = ticketSearch.trim().toLowerCase();

      const groups = Array.from(byLoan.values()).filter((g) => {
        const loan = g.loan;
        if (ticketQuery) {
          if (!(loan.voucher_no || "").toLowerCase().includes(ticketQuery))
            return false;
        }
        if (custQuery) {
          const name = (loan.customer_name || "").toLowerCase();
          const mobile = (loan.customer_mobile || "").toString().toLowerCase();
          if (!name.includes(custQuery) && !mobile.includes(custQuery))
            return false;
        }
        return true;
      });

      // Enrich each loan row with the ornament totals (article / pcs / weight /
      // valuation), which live only on the loan detail — bounded so a wide range
      // doesn't flood the backend. Customer + loan fields already arrived with
      // the receipts, so no per-row customer lookup is needed.
      const enriched = await mapWithConcurrency(groups, 6, async (g) => {
        const loan = g.loan;
        const detail = await loadTicketDetail(loan.voucher_no, loan.status);
        const custFromMap = customerMapRef.current.get(loan.cust_id);
        // Official mode masks any actual rate above the cap behind the master
        // interest % from settings, falling back to the cap itself when
        // settings are unavailable — it must NEVER print the true above-cap
        // rate. Unofficial mode always shows the actual.
        const actualRate =
          loan.loan_interest != null ? parseFloat(loan.loan_interest) : "";
        const masterInt = masterIntRef.current;
        const officialRate =
          masterInt != null && masterInt <= OFFICIAL_RATE_CAP
            ? masterInt
            : OFFICIAL_RATE_CAP;
        const displayRate =
          !wantUnofficial && actualRate > OFFICIAL_RATE_CAP
            ? officialRate
            : actualRate;
        // In-window receipts, oldest first — a register reads top-to-bottom in
        // payment order.
        const receipts = g.receipts
          .map((rc) => ({
            id: rc.id,
            date: rc.receipt_date,
            // Settlement ("H") receipts carry the H Form No; repayments blank.
            hformNo: rc.is_settlement ? (rc.voucher_ref || "").trim() : null,
            // Cumulative principal RECOVERED through this receipt = original
            // loan principal − the balance remaining after it. The column then
            // reads as the running principal deposited (55,000 → 58,000 →
            // 60,000) and the final / settlement row equals the loan principal,
            // so the user can eyeball that the loan is fully repaid. Uses the
            // absolute balance snapshot, so it stays correct even if earlier
            // receipts fall outside the selected date window.
            principal:
              rc.revised_principal != null
                ? (Number(loan.loan_amount) || 0) - Number(rc.revised_principal)
                : null,
            interest: Number(rc.interest_deducted) || 0,
          }))
          .sort((a, b) => {
            const d = (a.date || "").localeCompare(b.date || "");
            return d !== 0 ? d : (a.id || 0) - (b.id || 0);
          });
        // Receipt aggregates for the Summary view: total interest collected in
        // the window, and the final cumulative principal recovered (the last
        // receipt's running-principal value already IS the running total).
        const recTotalInterest = receipts.reduce(
          (s, x) => s + (x.interest || 0),
          0,
        );
        const lastPrincipal = [...receipts]
          .reverse()
          .find((x) => x.principal != null);
        const recPrincipalFinal = lastPrincipal
          ? lastPrincipal.principal
          : null;
        const floorId = detail.floorId;
        return {
          id: loan.pawn_id || loan.id,
          pledgeNo: loan.voucher_no,
          isSettled: !!loan.is_settled,
          loanDate: loan.loan_date,
          customerName:
            loan.customer_name ||
            detail.customer?.name ||
            custFromMap?.name ||
            "—",
          address:
            loan.customer_address ||
            detail.customer?.pickup_address ||
            custFromMap?.pickup_address ||
            "—",
          mobile:
            loan.customer_mobile ||
            detail.customer?.mobile ||
            custFromMap?.mobile ||
            "",
          principal: parseFloat(loan.loan_amount) || 0,
          interestRate: displayRate,
          description: detail.description,
          pcs: detail.pcs,
          grossWeight: detail.gross,
          valuation: detail.valuation,
          purity: detail.purity,
          rateDisplay: detail.rate,
          tareWeight: detail.tare,
          floorId,
          floorName:
            floorId != null
              ? floorMapRef.current.get(floorId) || `Floor ${floorId}`
              : "Unassigned",
          receipts,
          recTotalInterest,
          recPrincipalFinal,
        };
      });

      // Newest loan first.
      enriched.sort((a, b) => (b.id || 0) - (a.id || 0));
      setRows(enriched);
      if (enriched.length === 0) {
        showSnackbar("No records found for the selected filters.", "info");
      }
    } catch (err) {
      showSnackbar(
        err?.response?.data?.statusMessage || "Failed to load receipt report.",
        "error",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    storeId,
    pawnMode,
    stockFilter,
    dateMode,
    singleDate,
    fromDate,
    toDate,
    showAll,
    customerSearch,
    ticketSearch,
    loadCustomerMap,
    loadMasterInt,
    loadFloors,
    loadTicketDetail,
    showSnackbar,
  ]);

  // Auto-load on first mount, when the store resolves, when the pawn mode
  // flips, and — for a snappier UX — whenever a DATE is picked from the
  // calendar (single day, or From/To in range mode). The customer / pledge
  // text fields are deliberately NOT in the dependency list: those still load
  // only on the View button (or Enter) so typing doesn't fire a request per
  // keystroke.
  useEffect(() => {
    if (storeId != null) buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeId,
    pawnMode,
    stockFilter,
    dateMode,
    singleDate,
    fromDate,
    toDate,
    showAll,
  ]);

  const handleReset = () => {
    setDateMode("range");
    setSingleDate(TODAY());
    setFromDate(MONTH_START());
    setToDate(TODAY());
    setCustomerSearch("");
    setTicketSearch("");
    // Report options back to defaults (Rate on, everything else off).
    setShowAll(false);
    setHideTotal(false);
    setShowMobile(false);
    setShowRate(true);
    setShowPurity(false);
    setSummaryView(false);
    setShowTnC(false);
    setCrossedLoanPeriod(false);
    setFloorWiseSummary(false);
    setOnlySettleInterest(false);
    setAsPerDate(false);
    setNewFormat(false);
    setSettleDetailsAsOnDate(false);
    setBankPledge(false);
    setMaximumLoan(false);
    setReportVariant("");
    setRedemptionMonths(15);
    setSort(DEFAULT_SORT);
  };

  // ── Client-side view filters (no refetch) ──────────────────────────────────
  // "Crossed Loan Period": keep only loans whose redemption term has elapsed —
  // pledge date + 12 months is before today (i.e. the loan is overdue). Applied
  // over the fetched rows so toggling it is instant.
  const displayRows = useMemo(() => {
    let out = rows;
    if (crossedLoanPeriod) {
      const today = dayjs().startOf("day");
      out = out.filter((r) => {
        if (!r.loanDate) return false;
        return dayjs(r.loanDate).add(12, "month").isBefore(today);
      });
    }
    // Ordering is the last step, so it applies to whatever survived the filter.
    return sortRows(out, sort, ROW_SORT);
  }, [rows, crossedLoanPeriod, sort]);

  // ── Floor-wise summary (subtotals grouped by the loan's primary floor) ──────
  const floorSummary = useMemo(() => {
    if (!floorWiseSummary) return [];
    const byFloor = new Map();
    for (const r of displayRows) {
      const key = r.floorName || "Unassigned";
      if (!byFloor.has(key)) {
        byFloor.set(key, {
          floor: key,
          count: 0,
          principal: 0,
          gross: 0,
          valuation: 0,
          interest: 0,
        });
      }
      const g = byFloor.get(key);
      g.count += 1;
      g.principal += r.principal || 0;
      g.gross += r.grossWeight || 0;
      g.valuation += r.valuation || 0;
      g.interest += r.recTotalInterest || 0;
    }
    return Array.from(byFloor.values()).sort((a, b) =>
      a.floor.localeCompare(b.floor),
    );
  }, [floorWiseSummary, displayRows]);

  // ── Totals ────────────────────────────────────────────────────────────────
  // Only additive figures are totalled. Receipt "Principal" is a running
  // cumulative of principal recovered (its last row already IS the total for
  // that loan), so it is not summed in the footer.
  const totals = useMemo(() => {
    return displayRows.reduce(
      (acc, r) => {
        acc.principal += r.principal || 0;
        acc.pcs += r.pcs || 0;
        acc.gross += r.grossWeight || 0;
        acc.valuation += r.valuation || 0;
        acc.recInterest += (r.receipts || []).reduce(
          (s, x) => s + (x.interest || 0),
          0,
        );
        return acc;
      },
      { principal: 0, pcs: 0, gross: 0, valuation: 0, recInterest: 0 },
    );
  }, [displayRows]);

  // ── PDF export of the currently displayed report ───────────────────────────
  const generatePDF = () => {
    if (displayRows.length === 0) {
      showSnackbar("Nothing to print. Load some records first.", "warning");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;

    const periodLabel = showAll
      ? "All dates"
      : dateMode === "single"
      ? dayjs(singleDate).format("DD MMM YYYY")
      : `${dayjs(fromDate).format("DD MMM YYYY")} to ${dayjs(toDate).format(
          "DD MMM YYYY",
        )}`;

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text(currentStoreInfo?.name || "Receipt Report", pageWidth / 2, 12, {
      align: "center",
    });

    const stockLabel =
      stockFilter === "checked"
        ? "Stock Checked"
        : stockFilter === "unchecked"
        ? "Not Checked"
        : null;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      stockFilter
        ? `Stock Report — ${stockLabel} loans`
        : `Receipt Report — ${periodLabel}`,
      pageWidth / 2,
      18,
      { align: "center" },
    );

    const filterBits = [];
    if (customerSearch.trim())
      filterBits.push(`Customer: ${customerSearch.trim()}`);
    if (ticketSearch.trim()) filterBits.push(`Pledge: ${ticketSearch.trim()}`);
    if (crossedLoanPeriod) filterBits.push("Crossed loan period");
    const hasSettled = displayRows.some((r) => r.isSettled);
    doc.setFontSize(8);
    doc.text(
      `${displayRows.length} record${displayRows.length === 1 ? "" : "s"}${
        filterBits.length ? ` | ${filterBits.join(" | ")}` : ""
      }${hasSettled ? " | * = settled loan" : ""} | Generated ${dayjs().format(
        "DD MMM YYYY HH:mm",
      )}`,
      margin,
      24,
    );

    // Each receipt becomes a line inside the four sub-columns; blank when none.
    // In Summary view the four sub-columns collapse to a single aggregate line.
    const recLines = (r, field) => {
      if (!r.receipts || r.receipts.length === 0) return "-";
      if (summaryView) {
        if (field === "date")
          return `${r.receipts.length} rcpt${
            r.receipts.length === 1 ? "" : "s"
          }`;
        if (field === "hform") {
          const last = [...r.receipts].reverse().find((x) => x.hformNo);
          return last ? last.hformNo : "—";
        }
        if (field === "principal")
          return r.recPrincipalFinal == null
            ? "-"
            : fmtINR(r.recPrincipalFinal);
        if (field === "interest") return fmtINR(r.recTotalInterest);
        return "";
      }
      return r.receipts
        .map((x) => {
          if (field === "date")
            return x.date ? dayjs(x.date).format("DD/MM/YY") : "-";
          if (field === "hform") return x.hformNo || "—";
          if (field === "principal")
            return x.principal == null ? "-" : fmtINR(x.principal);
          if (field === "interest") return fmtINR(x.interest);
          return "";
        })
        .join("\n");
    };

    const body = displayRows.map((r, idx) => [
      idx + 1,
      `${r.isSettled ? "* " : ""}${r.pledgeNo || "-"}`,
      r.loanDate ? dayjs(r.loanDate).format("DD/MM/YYYY") : "-",
      // Mobile is appended under the customer/address when "Show Mobile" is on.
      `${r.customerName || "-"}\n${r.address || ""}${
        showMobile && r.mobile ? `\n${r.mobile}` : ""
      }`,
      fmtINR(r.principal),
      r.interestRate === "" || r.interestRate == null
        ? "-"
        : `${r.interestRate}%`,
      // Purity and the metal rate are folded into the Article cell in the PDF
      // when their toggle is on (keeps the fixed 17-column PDF layout intact —
      // the "Int. Rate" column above is the interest %, not the metal rate).
      `${r.description || "-"}${
        showPurity && r.purity ? `\n(${r.purity})` : ""
      }${showRate && r.rateDisplay ? `\n@ ${r.rateDisplay}/g` : ""}`,
      r.pcs == null ? "-" : r.pcs,
      r.grossWeight == null ? "-" : fmtWeight(r.grossWeight),
      r.valuation == null ? "-" : fmtINR(r.valuation),
      REDEMPTION_PERIOD,
      recLines(r, "date"),
      recLines(r, "hform"),
      recLines(r, "principal"),
      recLines(r, "interest"),
      "",
      "",
    ]);

    // The totals row is omitted when "Hide Total" is on.
    if (!hideTotal) {
      body.push([
        { content: "Totals", colSpan: 4, styles: { fontStyle: "bold" } },
        {
          content: fmtINR(totals.principal),
          styles: { fontStyle: "bold", halign: "right" },
        },
        { content: "", colSpan: 2 },
        {
          content: totals.pcs || "-",
          styles: { fontStyle: "bold", halign: "center" },
        },
        {
          content: fmtWeight(totals.gross),
          styles: { fontStyle: "bold", halign: "right" },
        },
        {
          content: fmtINR(totals.valuation),
          styles: { fontStyle: "bold", halign: "right" },
        },
        { content: "", colSpan: 3 },
        {
          content: fmtINR(totals.recInterest),
          styles: { fontStyle: "bold", halign: "right" },
        },
        { content: "", colSpan: 2 },
      ]);
    }

    doc.autoTable({
      startY: 30,
      head: [
        [
          { content: "#", rowSpan: 2 },
          { content: "Pledge No", rowSpan: 2 },
          { content: "Date of Loan", rowSpan: 2 },
          { content: "Customer & Address", rowSpan: 2 },
          { content: "Principal (Rs)", rowSpan: 2 },
          { content: "Int. Rate", rowSpan: 2 },
          { content: "Article", rowSpan: 2 },
          { content: "Pcs", rowSpan: 2 },
          { content: "Weight (g)", rowSpan: 2 },
          { content: "Value (Rs)", rowSpan: 2 },
          { content: "Redemption", rowSpan: 2 },
          { content: "Amount of Payments Received", colSpan: 4 },
          { content: "Owner (if not pawner)", rowSpan: 2 },
          { content: "Redeemer / Auction", rowSpan: 2 },
        ],
        ["Date", "H Form No", "Principal", "Interest"],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 6.5,
        cellPadding: 1,
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
        textColor: [15, 23, 42],
        valign: "top",
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 6.5,
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 7 },
        1: { halign: "left", cellWidth: 18 },
        2: { halign: "left", cellWidth: 17 },
        3: { halign: "left", cellWidth: 34 },
        4: { halign: "right", cellWidth: 18 },
        5: { halign: "right", cellWidth: 12 },
        6: { halign: "left", cellWidth: 26 },
        7: { halign: "center", cellWidth: 9 },
        8: { halign: "right", cellWidth: 16 },
        9: { halign: "right", cellWidth: 18 },
        10: { halign: "center", cellWidth: 16 },
        11: { halign: "left", cellWidth: 15 },
        12: { halign: "left", cellWidth: 14 },
        13: { halign: "right", cellWidth: 17 },
        14: { halign: "right", cellWidth: 16 },
        15: { halign: "left", cellWidth: 18 },
        16: { halign: "left", cellWidth: 18 },
      },
      margin: { left: margin, right: margin },
    });

    // FloorWise Summary — a compact subtotals-by-floor table beneath the main
    // register when the toggle is on.
    if (floorWiseSummary && floorSummary.length) {
      const afterY = (doc.lastAutoTable?.finalY || 30) + 6;
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Floor-wise Summary", margin, afterY);
      doc.autoTable({
        startY: afterY + 2,
        head: [
          [
            "Floor",
            "Loans",
            "Principal (Rs)",
            "Weight (g)",
            "Value (Rs)",
            "Interest (Rs)",
          ],
        ],
        body: floorSummary.map((g) => [
          g.floor,
          g.count,
          fmtINR(g.principal),
          fmtWeight(g.gross),
          fmtINR(g.valuation),
          fmtINR(g.interest),
        ]),
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.5, textColor: [15, 23, 42] },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7,
        },
        columnStyles: {
          1: { halign: "center" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
        },
        margin: { left: margin, right: margin },
        tableWidth: 130,
      });
    }

    // Terms & Conditions footnote when "T & C" is on.
    if (showTnC) {
      const tcY = (doc.lastAutoTable?.finalY || 30) + 6;
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      doc.text("Terms & Conditions", margin, tcY);
      doc.setFont("helvetica", "normal");
      doc.text(
        [
          "1. Pledged articles are redeemable within the agreed 12-month period.",
          "2. Interest is charged per the applicable scheme / master rate.",
          "3. Articles unredeemed beyond the loan period are liable for auction after notice.",
          "4. The pledge card / receipt must be produced at the time of redemption.",
        ],
        margin,
        tcY + 4,
      );
    }

    const fileLabel = stockFilter
      ? stockFilter
      : dateMode === "single"
      ? dayjs(singleDate).format("YYYY-MM-DD")
      : `${dayjs(fromDate).format("YYYY-MM-DD")}_to_${dayjs(toDate).format(
          "YYYY-MM-DD",
        )}`;
    doc.save(`${stockFilter ? "Stock" : "Receipt"}_Report_${fileLabel}.pdf`);
  };

  // ── Cell styling ────────────────────────────────────────────────────────────
  const headCellSx = {
    bgcolor: "#F8FAFC",
    color: INK_MUTED,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: "nowrap",
  };
  const cellSx = {
    fontSize: "0.78rem",
    color: INK_SOFT,
    borderBottom: `1px solid ${BORDER}`,
    py: 1,
    verticalAlign: "top",
  };
  // A single line inside one of the receipt sub-columns.
  const recLineSx = {
    fontSize: "0.76rem",
    color: INK_SOFT,
    py: 0.35,
    borderBottom: `1px dashed ${BORDER}`,
    "&:last-child": { borderBottom: "none" },
  };

  // ── Report-option checkbox helper ───────────────────────────────────────────
  // `hint` marks a legacy toggle that is rendered for parity but not yet wired
  // to the report output — it gets a tooltip and italic label so the distinction
  // is visible to the user.
  const NOT_WIRED = "Rendered for parity — behaviour not wired yet";
  const optCheck = (label, checked, setter, hint) => {
    const ctrl = (
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={(e) => setter(e.target.checked)}
            sx={{
              p: 0.5,
              color: INK_MUTED,
              "&.Mui-checked": { color: BLUE },
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontSize: "0.76rem",
              color: hint ? INK_MUTED : INK_SOFT,
              fontStyle: hint ? "italic" : "normal",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        }
        sx={{ m: 0, mr: 0.5 }}
      />
    );
    return hint ? (
      <Tooltip key={label} title={hint} arrow placement="top">
        {ctrl}
      </Tooltip>
    ) : (
      ctrl
    );
  };

  // Stacks one receipt field vertically so all four sub-columns line up.
  const ReceiptStack = ({ receipts, render, align = "left" }) => {
    if (!receipts || receipts.length === 0)
      return <Box sx={{ ...recLineSx, textAlign: align }}>—</Box>;
    return (
      <>
        {receipts.map((x, i) => (
          <Box key={x.id ?? i} sx={{ ...recLineSx, textAlign: align }}>
            {render(x)}
          </Box>
        ))}
      </>
    );
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      {/* ── Header (hidden when embedded in the unified Reports screen) ── */}
      {!embedded && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            <RegisterIcon />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              Receipt Report
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: INK_MUTED }}>
              {currentStoreInfo?.name ? `${currentStoreInfo.name}` : ""}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Stock Report banner (only when opened from the Stock Check screen) ── */}
      {stockFilter && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${BLUE}`,
            bgcolor: BLUE_PALE,
            borderRadius: 3,
            mb: 2,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StockIcon sx={{ color: BLUE, fontSize: 20 }} />
              <Typography
                sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: "0.9rem" }}
              >
                Stock Report —{" "}
                {stockFilter === "checked"
                  ? "Stock Checked loans"
                  : "Not Checked loans"}
              </Typography>
              <Chip
                size="small"
                label={`${displayRows.length} ${
                  displayRows.length === 1 ? "loan" : "loans"
                }`}
                sx={{ bgcolor: SURFACE, color: BLUE_DARK, fontWeight: 700 }}
              />
            </Box>
            {!stockFilterProp && (
              <Button
                size="small"
                onClick={clearStock}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: BLUE_DARK,
                }}
              >
                Exit stock report
              </Button>
            )}
          </Box>
        </Card>
      )}

      {/* ── Filters ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          mb: 2,
          bgcolor: SURFACE,
          ...reportFixedSx,
        }}
      >
        <CardContent
          sx={{
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            "&:last-child": { pb: { xs: 1.5, md: 2 } },
          }}
        >
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
            {/* Date filters — irrelevant in Stock Report mode (all dates shown). */}
            {!stockFilter && (
              <>
                {/* Date window: "Single Day" ticked = one field; unticked
                    slides a "To" field open beside it. */}
                <ReportDateWindow
                  label="Receipt Date"
                  dateMode={dateMode}
                  setDateMode={setDateMode}
                  singleDate={singleDate}
                  setSingleDate={setSingleDate}
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                />
              </>
            )}

            {/* Customer search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Customer"
                placeholder="Name or mobile"
                size="small"
                fullWidth
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: INK_MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Pledge search */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Pledge No"
                placeholder="Pledge / voucher no."
                size="small"
                fullWidth
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buildReport()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptLongIcon
                        sx={{ fontSize: 18, color: INK_MUTED }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Actions */}
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={buildReport}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{
                    bgcolor: BLUE,
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
                  }}
                >
                  View
                </Button>
                <ReportSortControl
                  options={SORT_OPTIONS}
                  value={sort}
                  onChange={setSort}
                />
                <Tooltip title="Reset filters">
                  <IconButton
                    onClick={handleReset}
                    sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
                  >
                    <RefreshIcon sx={{ color: INK_MUTED }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* ── Report options toolbar (ported from the legacy VB pledge book) ── */}
          <Divider sx={{ my: 1.5 }} />
          <FormGroup row sx={{ rowGap: 0.25, columnGap: 1 }}>
            {/* Fully wired — each changes the register output. */}
            {optCheck("Show all", showAll, toggleShowAll)}
            {optCheck("Hide Total", hideTotal, setHideTotal)}
            {optCheck("Show Mobile", showMobile, setShowMobile)}
            {optCheck("Show Rate", showRate, setShowRate)}
            {optCheck("Show Purity", showPurity, setShowPurity)}
            {optCheck("Summary", summaryView, setSummaryView)}
            {optCheck("T & C", showTnC, setShowTnC)}
            {optCheck(
              "Crossed Loan Period",
              crossedLoanPeriod,
              setCrossedLoanPeriod,
            )}
            {optCheck(
              "FloorWise Summary",
              floorWiseSummary,
              setFloorWiseSummary,
            )}
            {/* Rendered + state only — legacy toggles pending a spec. */}
            {optCheck(
              "Only Settle Interest",
              onlySettleInterest,
              setOnlySettleInterest,
              NOT_WIRED,
            )}
            {optCheck("As Per Date", asPerDate, setAsPerDate, NOT_WIRED)}
            {optCheck("NewFormat", newFormat, setNewFormat, NOT_WIRED)}
            {optCheck(
              "Settle Details As On Date",
              settleDetailsAsOnDate,
              setSettleDetailsAsOnDate,
              NOT_WIRED,
            )}
            {optCheck("Bank Pledge", bankPledge, setBankPledge, NOT_WIRED)}
            {optCheck("Maximum loan", maximumLoan, setMaximumLoan, NOT_WIRED)}
          </FormGroup>
        </CardContent>
      </Card>

      {/* ── FloorWise Summary (subtotals by the loan's primary floor) ── */}
      {floorWiseSummary && floorSummary.length > 0 && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: 3,
            mb: 2,
            bgcolor: SURFACE,
            ...reportFixedSx,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <FloorIcon sx={{ color: BLUE, fontSize: 20 }} />
            <Typography
              sx={{ fontWeight: 700, color: INK, fontSize: "0.9rem" }}
            >
              Floor-wise Summary
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Floor</TableCell>
                  <TableCell align="center" sx={headCellSx}>
                    Loans
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Principal (₹)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Weight (g)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Value (₹)
                  </TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Interest (₹)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {floorSummary.map((g) => (
                  <TableRow key={g.floor} hover>
                    <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                      {g.floor}
                    </TableCell>
                    <TableCell align="center" sx={cellSx}>
                      {g.count}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtINR(g.principal)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtWeight(g.gross)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtINR(g.valuation)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {fmtINR(g.interest)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* ── View section (results) ── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
          bgcolor: SURFACE,
          ...reportResultsCardSx,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: `1px solid ${BORDER}`,
            ...reportFixedSx,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 700, color: INK, fontSize: "0.95rem" }}
            >
              Receipts Register
            </Typography>
            {displayRows.some((r) => r.isSettled) && (
              <Typography sx={{ fontSize: "0.7rem", color: INK_MUTED }}>
                <Box component="span" sx={{ color: SUCCESS, fontWeight: 900 }}>
                  *
                </Box>{" "}
                marks a settled / closed loan
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={`${displayRows.length} ${
                displayRows.length === 1 ? "record" : "records"
              }`}
              size="small"
              sx={{
                bgcolor: BLUE_PALE,
                color: BLUE_DARK,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={generatePDF}
              disabled={loading || displayRows.length === 0}
              startIcon={<PdfIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: BLUE,
                borderColor: BLUE,
                "&:hover": { borderColor: BLUE_DARK, bgcolor: BLUE_PALE },
              }}
            >
              Print PDF
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : displayRows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <RegisterIcon sx={{ fontSize: 44, color: BORDER, mb: 1 }} />
            <Typography sx={{ color: INK_MUTED, fontSize: "0.9rem" }}>
              {crossedLoanPeriod
                ? "No loans have crossed their redemption period in this range."
                : "No records to show. Adjust the filters and press View."}
            </Typography>
          </Box>
        ) : (
          /* This 17-column register scrolls horizontally too, so it uses the
             variant that keeps the horizontal scrollbar grabbable. */
          <TableContainer sx={reportWideTableSx}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                {/* Row 1 — group header */}
                <TableRow>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    #
                  </TableCell>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Pledge No
                  </TableCell>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Date of Loan
                  </TableCell>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Customer &amp; Address
                  </TableCell>
                  <TableCell rowSpan={2} align="right" sx={headCellSx}>
                    Principal (₹)
                  </TableCell>
                  {showRate && (
                    <TableCell rowSpan={2} align="right" sx={headCellSx}>
                      Rate (₹/g)
                    </TableCell>
                  )}
                  {showPurity && (
                    <TableCell rowSpan={2} align="right" sx={headCellSx}>
                      Purity
                    </TableCell>
                  )}
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Article
                  </TableCell>
                  <TableCell rowSpan={2} align="center" sx={headCellSx}>
                    Pcs
                  </TableCell>
                  <TableCell rowSpan={2} align="right" sx={headCellSx}>
                    Weight (g)
                  </TableCell>
                  <TableCell rowSpan={2} align="right" sx={headCellSx}>
                    Value (₹)
                  </TableCell>
                  <TableCell rowSpan={2} align="center" sx={headCellSx}>
                    Redemption
                  </TableCell>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      ...headCellSx,
                      borderLeft: `1px solid ${BORDER}`,
                      borderRight: `1px solid ${BORDER}`,
                      color: BLUE_DARK,
                    }}
                  >
                    Amount of Payments Received
                  </TableCell>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Owner (if not pawner)
                  </TableCell>
                  <TableCell rowSpan={2} sx={headCellSx}>
                    Redeemer / Auction
                  </TableCell>
                </TableRow>
                {/* Row 2 — payment sub-columns */}
                <TableRow>
                  <TableCell
                    sx={{ ...headCellSx, borderLeft: `1px solid ${BORDER}` }}
                  >
                    Date
                  </TableCell>
                  <TableCell sx={headCellSx}>H Form No</TableCell>
                  <TableCell align="right" sx={headCellSx}>
                    Principal
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...headCellSx, borderRight: `1px solid ${BORDER}` }}
                  >
                    Interest
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ "&:hover": { bgcolor: BLUE_PALE } }}
                  >
                    <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        fontWeight: 700,
                        color: INK,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.isSettled && (
                        <Tooltip title="Loan settled / closed">
                          <Box
                            component="span"
                            sx={{
                              color: SUCCESS,
                              fontWeight: 900,
                              mr: 0.5,
                              cursor: "default",
                            }}
                          >
                            *
                          </Box>
                        </Tooltip>
                      )}
                      {r.pledgeNo}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                      {r.loanDate
                        ? dayjs(r.loanDate).format("DD MMM YYYY")
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 220 }}>
                      <Typography
                        sx={{ fontSize: "0.8rem", fontWeight: 700, color: INK }}
                      >
                        {r.customerName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.72rem", color: INK_MUTED }}
                      >
                        {r.address}
                      </Typography>
                      {showMobile && r.mobile && (
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: INK_SOFT,
                            fontWeight: 600,
                          }}
                        >
                          {r.mobile}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellSx, fontWeight: 600 }}
                    >
                      {fmtINR(r.principal)}
                    </TableCell>
                    {showRate && (
                      <TableCell align="right" sx={cellSx}>
                        {r.rateDisplay == null ? "—" : r.rateDisplay}
                      </TableCell>
                    )}
                    {showPurity && (
                      <TableCell align="right" sx={cellSx}>
                        {r.purity == null ? "—" : r.purity}
                      </TableCell>
                    )}
                    <TableCell sx={{ ...cellSx, minWidth: 120 }}>
                      {r.description}
                    </TableCell>
                    <TableCell align="center" sx={cellSx}>
                      {r.pcs == null ? "—" : r.pcs}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {r.grossWeight == null ? "—" : fmtWeight(r.grossWeight)}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {r.valuation == null ? "—" : fmtINR(r.valuation)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ ...cellSx, whiteSpace: "nowrap" }}
                    >
                      {REDEMPTION_PERIOD}
                    </TableCell>
                    {/* Payments received — four aligned sub-columns. In Summary
                        view each column collapses to a single aggregate line. */}
                    <TableCell
                      sx={{
                        ...cellSx,
                        p: 0,
                        borderLeft: `1px solid ${BORDER}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {summaryView ? (
                        <Box sx={recLineSx}>
                          {r.receipts.length
                            ? `${r.receipts.length} rcpt${
                                r.receipts.length === 1 ? "" : "s"
                              }`
                            : "—"}
                        </Box>
                      ) : (
                        <ReceiptStack
                          receipts={r.receipts}
                          render={(x) =>
                            x.date ? dayjs(x.date).format("DD MMM YYYY") : "—"
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, p: 0 }}>
                      {summaryView ? (
                        <Box sx={recLineSx}>
                          {[...r.receipts].reverse().find((x) => x.hformNo)
                            ?.hformNo || "—"}
                        </Box>
                      ) : (
                        <ReceiptStack
                          receipts={r.receipts}
                          render={(x) => x.hformNo || "—"}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellSx, p: 0 }}>
                      {summaryView ? (
                        <Box sx={{ ...recLineSx, textAlign: "right" }}>
                          {r.recPrincipalFinal == null
                            ? "—"
                            : fmtINR(r.recPrincipalFinal)}
                        </Box>
                      ) : (
                        <ReceiptStack
                          receipts={r.receipts}
                          align="right"
                          render={(x) =>
                            x.principal == null ? "—" : fmtINR(x.principal)
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...cellSx,
                        p: 0,
                        borderRight: `1px solid ${BORDER}`,
                      }}
                    >
                      {summaryView ? (
                        <Box sx={{ ...recLineSx, textAlign: "right" }}>
                          {r.receipts.length ? fmtINR(r.recTotalInterest) : "—"}
                        </Box>
                      ) : (
                        <ReceiptStack
                          receipts={r.receipts}
                          align="right"
                          render={(x) => fmtINR(x.interest)}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>—</TableCell>
                    <TableCell sx={cellSx}>—</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {!hideTotal && (
                <TableFooter>
                  <TableRow>
                    {(() => {
                      const footBase = {
                        position: "sticky",
                        bottom: 0,
                        bgcolor: "#F8FAFC",
                        fontWeight: 800,
                        color: INK,
                        fontSize: "0.8rem",
                        borderTop: `2px solid ${BORDER}`,
                      };
                      return (
                        <>
                          <TableCell colSpan={4} sx={footBase}>
                            Totals
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...footBase, color: SUCCESS }}
                          >
                            {fmtINR(totals.principal)}
                          </TableCell>
                          {showRate && <TableCell sx={footBase} />}
                          {showPurity && <TableCell sx={footBase} />}
                          <TableCell sx={footBase} />
                          <TableCell align="center" sx={footBase}>
                            {totals.pcs || "—"}
                          </TableCell>
                          <TableCell align="right" sx={footBase}>
                            {fmtWeight(totals.gross)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...footBase, color: SUCCESS }}
                          >
                            {fmtINR(totals.valuation)}
                          </TableCell>
                          <TableCell sx={footBase} />
                          <TableCell
                            sx={{
                              ...footBase,
                              borderLeft: `1px solid ${BORDER}`,
                            }}
                          />
                          <TableCell sx={footBase} />
                          <TableCell sx={footBase} />
                          <TableCell
                            align="right"
                            sx={{
                              ...footBase,
                              color: SUCCESS,
                              borderRight: `1px solid ${BORDER}`,
                            }}
                          >
                            {fmtINR(totals.recInterest)}
                          </TableCell>
                          <TableCell sx={footBase} />
                          <TableCell sx={footBase} />
                        </>
                      );
                    })()}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        )}

        {/* ── Terms & Conditions footnote (when "T & C" is on) ── */}
        {showTnC && displayRows.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${BORDER}` }}>
            <Typography
              sx={{ fontSize: "0.72rem", fontWeight: 800, color: INK, mb: 0.5 }}
            >
              Terms &amp; Conditions
            </Typography>
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: 2.5,
                fontSize: "0.7rem",
                color: INK_MUTED,
                "& li": { mb: 0.25 },
              }}
            >
              <li>
                Pledged articles are redeemable within the agreed 12-month
                period.
              </li>
              <li>
                Interest is charged per the applicable scheme / master rate.
              </li>
              <li>
                Articles unredeemed beyond the loan period are liable for
                auction after notice.
              </li>
              <li>
                The pledge card / receipt must be produced at the time of
                redemption.
              </li>
            </Box>
          </Box>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceiptReport;
